import { useCallback, useState } from "react";

import { isAPIError, readServiceError } from "@/core/services";
import { ignorarConflito, remanejarProfissional } from "@/modules/carga-trabalho";
import type { IgnorarConflitoParams, RemanejarProfissionalParams } from "@/modules/carga-trabalho";

export const REMANEJAR_SUCCESS_MESSAGE =
  "Profissional substituído com sucesso. Conflito de alocação resolvido.";
export const REMANEJAR_ALOCACAO_CONFLICT_MESSAGE =
  "O profissional selecionado já possui alocação no período.";
export const REMANEJAR_ERROR_MESSAGE =
  "Não foi possível substituir o profissional. Tente novamente.";
export const REMANEJAR_SUBSTITUTO_INDISPONIVEL_MESSAGE =
  "O profissional selecionado não está mais disponível. Escolha outro substituto.";
export const REMANEJAR_NOT_FOUND_MESSAGE =
  "A alocação não foi encontrada. Atualize a carga de trabalho e tente novamente.";

export const IGNORAR_SUCCESS_MESSAGE =
  "Conflito marcado como tratado. A sobreposição agora aparece em verde no cronograma.";
export const IGNORAR_ERROR_MESSAGE = "Não foi possível registrar a justificativa. Tente novamente.";
export const IGNORAR_ALREADY_REGISTERED_MESSAGE =
  "Este conflito já foi registrado como ignorado. Atualize a carga de trabalho.";

/** `hub.equipe:edit` é separado do de leitura (`hub.equipe:view`): ver o conflito não basta. */
export const RESOLVER_CONFLITO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para resolver conflitos de alocação.";

/**
 * Motivo da recusa: 409 (substituto indisponível no período, `PROFISSIONAL_INDISPONIVEL`, ou conflito já registrado),
 * 403, 404, 400 (vai no campo justificativa) ou qualquer outra falha.
 */
export type ResolverConflitoFailureKind =
  "conflict" | "unavailable" | "forbidden" | "notFound" | "invalid" | "error";

export interface ResolverConflitoFailure {
  ok: false;
  kind: ResolverConflitoFailureKind;
  message: string;
}

export type ResolverConflitoResult = { ok: true } | ResolverConflitoFailure;

interface FailureMessages {
  conflict: string;
  notFound: string;
  error: string;
  /** No 409, vale a mensagem do servidor; `conflict` fica de fallback. */
  serverConflictMessage?: boolean;
  /** Remanejar: 409 `PROFISSIONAL_INDISPONIVEL` vira `unavailable`. */
  detectUnavailable?: boolean;
}

function isSubstitutoIndisponivel(error: Parameters<typeof readServiceError>[0]): boolean {
  const { code, message } = readServiceError(error);
  return (
    code === "PROFISSIONAL_INDISPONIVEL" ||
    (code === "" && message === REMANEJAR_SUBSTITUTO_INDISPONIVEL_MESSAGE)
  );
}

function toFailure(error: unknown, messages: FailureMessages): ResolverConflitoFailure {
  const failure = (kind: ResolverConflitoFailureKind, message: string) =>
    ({ ok: false, kind, message }) as const;

  if (!isAPIError(error)) return failure("error", messages.error);

  switch (error.status) {
    case 409:
      if (messages.detectUnavailable && isSubstitutoIndisponivel(error)) {
        return failure("unavailable", REMANEJAR_SUBSTITUTO_INDISPONIVEL_MESSAGE);
      }
      return failure(
        "conflict",
        (messages.serverConflictMessage && readServiceError(error).message) || messages.conflict,
      );
    case 403:
      return failure("forbidden", RESOLVER_CONFLITO_FORBIDDEN_MESSAGE);
    case 404:
      return failure("notFound", messages.notFound);
    case 400:
      // O bff pode embrulhar o 400 do core: vale a mensagem do core.
      return failure("invalid", readServiceError(error).message || messages.error);
    default:
      return failure("error", messages.error);
  }
}

/** Nunca rejeita: devolve o resultado para o modal decidir onde mostrar o erro. */
function useResolverAction<P>(
  action: (params: P) => Promise<void>,
  messages: FailureMessages,
): { submit: (params: P) => Promise<ResolverConflitoResult>; isSubmitting: boolean } {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    conflict,
    notFound,
    error: errorMessage,
    serverConflictMessage,
    detectUnavailable,
  } = messages;

  const submit = useCallback(
    async (params: P): Promise<ResolverConflitoResult> => {
      setIsSubmitting(true);
      try {
        await action(params);
        return { ok: true };
      } catch (error) {
        return toFailure(error, {
          conflict,
          notFound,
          error: errorMessage,
          serverConflictMessage,
          detectUnavailable,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [action, conflict, notFound, errorMessage, serverConflictMessage, detectUnavailable],
  );

  return { submit, isSubmitting };
}

export function useRemanejarProfissional() {
  const { submit, isSubmitting } = useResolverAction<RemanejarProfissionalParams>(
    remanejarProfissional,
    {
      conflict: REMANEJAR_ALOCACAO_CONFLICT_MESSAGE,
      notFound: REMANEJAR_NOT_FOUND_MESSAGE,
      error: REMANEJAR_ERROR_MESSAGE,
      serverConflictMessage: true,
      detectUnavailable: true,
    },
  );
  return { remanejar: submit, isSubmitting };
}

export function useIgnorarConflito() {
  const { submit, isSubmitting } = useResolverAction<IgnorarConflitoParams>(ignorarConflito, {
    conflict: IGNORAR_ALREADY_REGISTERED_MESSAGE,
    notFound: IGNORAR_ERROR_MESSAGE,
    error: IGNORAR_ERROR_MESSAGE,
  });
  return { ignorar: submit, isSubmitting };
}
